import { toast } from "sonner";

/**
 * Shared helper for toasts that carry an action button (UX audit 2.4).
 *
 * Why this exists: sonner's default duration is 4s, which is too short for a
 * user to read the message AND reach the action button ("View", "Undo",
 * "Sign in"...). Any toast with an action must live >= 8 seconds. Sonner
 * already pauses the dismiss timer while the pointer is over the toast stack
 * (built-in `onMouseEnter`/`onMouseMove` expanded state), and the mounted
 * <Toaster> in src/components/ui/sonner.tsx sets `pauseWhenPageIsHidden` so
 * background tabs don't eat the window either.
 *
 * Dedupe: pass a stable `id` and repeat fires (e.g. a mutation chain that
 * re-emits the same success) update the existing toast in place instead of
 * stacking a duplicate on top of it.
 *
 * Related helpers that predate this one:
 * - src/lib/undo-toast.ts  — undo-specific wrapper (already 8s default)
 * - src/lib/toast-utils.ts — plain success/error/info without actions
 */

/** Minimum lifetime for toasts that carry an action button. */
export const ACTION_TOAST_DURATION_MS = 8000;

export type ActionToastVariant =
  | "success"
  | "error"
  | "warning"
  | "info"
  | "message";

export interface ToastActionConfig {
  /** Button label, e.g. "View" or "Undo". */
  label: string;
  onClick: () => void | Promise<void>;
}

export interface ToastWithActionOptions {
  /** The action button. If you don't have one, use plain sonner instead. */
  action: ToastActionConfig;
  description?: string;
  /**
   * Stable id for dedupe — a second fire with the same id replaces the
   * existing toast instead of stacking. Scope it to the entity, e.g.
   * `request-status-${id}`.
   */
  id?: string;
  /** Override only to go LONGER than the 8s floor (clamped to the floor). */
  duration?: number;
  /** Visual variant; defaults to "success". */
  variant?: ActionToastVariant;
}

export function toastWithAction(
  message: string,
  {
    action,
    description,
    id,
    duration = ACTION_TOAST_DURATION_MS,
    variant = "success",
  }: ToastWithActionOptions,
) {
  const options = {
    description,
    // Never let an action toast dip below the 8s floor.
    duration: Math.max(duration, ACTION_TOAST_DURATION_MS),
    action: {
      label: action.label,
      onClick: () => {
        void action.onClick();
      },
    },
    ...(id ? { id } : {}),
  };

  switch (variant) {
    case "error":
      return toast.error(message, options);
    case "warning":
      return toast.warning(message, options);
    case "info":
      return toast.info(message, options);
    case "message":
      return toast(message, options);
    case "success":
    default:
      return toast.success(message, options);
  }
}
